import { fieldsOf, isPlainRecord, listOf, pathTo, report, whole, type Place } from "./checks.ts";
import { MAX_HEAL_AMOUNT, type AbilityShape, type Restore, type Shorten } from "./types.ts";

/**
 * An ability's stamina restore and its shortens (ADR-091's note for P2.8-T57, item 1): what a record aimed at oneself or
 * an ally restores of stamina at once, and the effects whose time left it cuts by a percent. Both land only on oneself
 * or an ally, as a heal does; a full cure is `cleanses`, so a shorten's percent stops at 99.
 */

/**
 * The highest percent a shorten cuts: below a whole cure, which `cleanses` is.
 */
export const MAX_SHORTEN_PERCENT = 99;

/**
 * Reports a field that lands only on oneself or an ally, carried by any other shape.
 *
 * @param place Where to report.
 * @param shape The ability's shape, null when it was wrong.
 * @param what The field as the message names it.
 * @returns True when the shape may carry it.
 */
function helpingShape(place: Place, shape: AbilityShape | null, what: string): boolean {
  if (shape !== null && shape.kind !== "ally" && shape.kind !== "self") {
    report(place, `${what} lands on oneself or an ally, and this shape is a ${shape.kind}`);
    return false;
  }
  return true;
}

/**
 * Checks an ability's `restoreStamina`: null for none, or a whole amount from 1 to `MAX_HEAL_AMOUNT`, which only a shape
 * that lands on oneself or an ally may carry.
 *
 * @param place Where to report.
 * @param value The restore.
 * @param shape The ability's shape, null when it was wrong.
 * @returns The restore, null for none, or undefined when it is missing or wrong.
 */
export function restoreStaminaOf(place: Place, value: unknown, shape: AbilityShape | null): Restore | null | undefined {
  if (value === null) {
    return null;
  }
  if (value === undefined) {
    return undefined;
  }
  if (!isPlainRecord(value)) {
    report(place, "restoreStamina is not a record");
    return undefined;
  }
  const fields = fieldsOf(place, value, "restoreStamina", ["amount"], "a restore") as Readonly<Record<string, unknown>>;
  const amount = whole(place, fields["amount"], "restoreStamina.amount", 1, MAX_HEAL_AMOUNT);
  if (!helpingShape(place, shape, "a stamina restore")) {
    return undefined;
  }
  return amount === null || Object.keys(fields).length !== 1 ? undefined : { amount };
}

/**
 * Checks an ability's `shortens`: a list of an effect the table holds, at most once and never one the ability
 * cleanses, with a whole percent from 1 to `MAX_SHORTEN_PERCENT`; only a shape that lands on oneself or an ally may
 * carry any.
 *
 * @param place Where to report.
 * @param value The list.
 * @param effectIds The ids the effects table holds.
 * @param cleanses The ids the ability cleanses, or null when they were wrong.
 * @param shape The ability's shape, null when it was wrong.
 * @returns The entries, or null when the list is missing or wrong.
 */
export function shortensOf(place: Place, value: unknown, effectIds: ReadonlySet<string>, cleanses: readonly string[] | null, shape: AbilityShape | null): Shorten[] | null {
  const list = listOf(place, value, "shortens");
  if (list === null) {
    return null;
  }
  let ok = true;
  const shortens: Shorten[] = [];
  for (const [index, item] of list.entries()) {
    const path = pathTo("shortens", index, true);
    if (!isPlainRecord(item)) {
      report(place, `${path} is not a record`);
      ok = false;
      continue;
    }
    const fields = fieldsOf(place, item, path, ["effect", "percent"], "a shorten") as Readonly<Record<string, unknown>>;
    const effect = fields["effect"];
    const percent = whole(place, fields["percent"], `${path}.percent`, 1, MAX_SHORTEN_PERCENT);
    if (typeof effect !== "string" || !effectIds.has(effect)) {
      report(place, `${path}.effect names ${String(effect)}, which the effects table does not hold`);
      ok = false;
    } else if (shortens.some((entry) => entry.effect === effect)) {
      report(place, `shortens names ${effect} twice`);
      ok = false;
    } else if (cleanses?.includes(effect) === true) {
      report(place, `shortens ${effect}, which it cleanses`);
      ok = false;
    } else if (percent !== null && Object.keys(fields).length === 2) {
      shortens.push({ effect, percent });
    } else {
      ok = false;
    }
  }
  if (shortens.length > 0 && !helpingShape(place, shape, "a shorten")) {
    return null;
  }
  return ok ? shortens : null;
}
